import React, { FC, useContext, useMemo, useState, useEffect } from 'react'
import { Typography, Flex, DatePicker, Spin } from 'antd'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from 'recharts'
import dayjs from 'dayjs'
import { VerticalContext } from 'store/verticals/Context'
import { useCurrency } from 'contexts'
import { useCurrencyConversion } from 'hooks/useCurrencyConversion'
import { SectionCard, LegendDot, formatAbbreviatedCurrency, formatYAxis } from 'containers/VerticalPage/common/antd'
import { getTotalRevenueFromEntity } from './utils'
import { getDefaultDateValues } from 'containers/VerticalPage/common/utils'

const { Text } = Typography
const { RangePicker } = DatePicker

const CHART_COLORS = {
  reported: '#1B2559',
  documented: '#2196F3',
  estimated: '#90CAF9',
}

const LABEL_STYLE: React.CSSProperties = {
  fontFamily: 'Roboto',
  fontWeight: 400,
  fontSize: 16,
  lineHeight: '24px',
}

const VALUE_STYLE: React.CSSProperties = {
  fontFamily: 'Roboto',
  fontWeight: 500,
  fontSize: 16,
  lineHeight: '24px',
}

const Overview: FC = () => {
  const { verticalEntity } = useContext(VerticalContext)
  const { convertCurrency, currentCurrency } = useCurrency()
  const { formatAmount } = useCurrencyConversion()
  const [chartData, setChartData] = useState<any[]>([])

  const defaultDate = getDefaultDateValues()

  useEffect(() => {
    const fetchChartData = async () => {
      if (!verticalEntity || !convertCurrency) return
      const data = await getTotalRevenueFromEntity(
        verticalEntity,
        { start: defaultDate.start, end: defaultDate.end },
        false,
        convertCurrency,
        currentCurrency,
      )
      setChartData(data)
    }
    fetchChartData()
  }, [verticalEntity, convertCurrency, currentCurrency]) // eslint-disable-line react-hooks/exhaustive-deps

  const dataBounds = useMemo<[dayjs.Dayjs, dayjs.Dayjs] | null>(() => {
    if (!chartData.length) return null
    const years = chartData.map(d => parseInt(d.date as string, 10)).filter(y => !isNaN(y))
    if (!years.length) return null
    return [dayjs().year(Math.min(...years)), dayjs().year(Math.max(...years))]
  }, [chartData])

  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null])

  const effectiveRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] = [
    dateRange[0] ?? dataBounds?.[0] ?? null,
    dateRange[1] ?? dataBounds?.[1] ?? null,
  ]

  const filteredChartData = useMemo(() => {
    const [start, end] = effectiveRange
    if (!start || !end) return chartData
    const startYear = start.year()
    const endYear = end.year()
    return chartData.filter(d => {
      const y = parseInt(d.date as string, 10)
      return y >= startYear && y <= endYear
    })
  }, [chartData, effectiveRange])

  const totalRevenue = useMemo(
    () => filteredChartData.reduce((acc, item) => acc + (Number(item.total) || 0), 0),
    [filteredChartData],
  )

  const unreportedRevenue = useMemo(
    () => filteredChartData.reduce((acc, item) => acc + (Number(item.unreported) || 0), 0),
    [filteredChartData],
  )

  const hasEstimated = filteredChartData.some(item => item.estimated)

  const legendTotals = useMemo(() => {
    const reported = filteredChartData.reduce((acc, item) => acc + (Number(item.reported) || 0), 0)
    const documented = filteredChartData.reduce((acc, item) => acc + (Number(item.documented) || 0), 0)
    const estimated = filteredChartData.reduce((acc, item) => acc + (Number(item.estimated) || 0), 0)
    return { reported, documented, estimated }
  }, [filteredChartData])

  const legendItems = useMemo(() => {
    const fmt = (v: number) => (formatAmount ? formatAmount(v, currentCurrency) : formatAbbreviatedCurrency(v))
    const items = [
      { color: CHART_COLORS.reported, label: `Reported: ${fmt(legendTotals.reported)}` },
      { color: CHART_COLORS.documented, label: `Documented: ${fmt(legendTotals.documented)}` },
    ]
    if (hasEstimated) {
      items.push({ color: CHART_COLORS.estimated, label: `Estimated: ${fmt(legendTotals.estimated)}` })
    }
    return items
  }, [hasEstimated, legendTotals, formatAmount, currentCurrency])

  if (!verticalEntity) {
    return (
      <SectionCard>
        <Flex justify='center' align='center' style={{ height: 300 }}>
          <Spin />
        </Flex>
      </SectionCard>
    )
  }

  return (
    <SectionCard
      title={
        <Flex justify='space-between' align='center'>
          <span>
            Total Revenue :{' '}
            <span style={{ fontWeight: 500 }}>
              {formatAmount ? formatAmount(totalRevenue, currentCurrency) : formatAbbreviatedCurrency(totalRevenue)}
            </span>{' '}
            | Unreported Revenue{' '}
            <span style={{ fontWeight: 500 }}>
              {formatAmount
                ? formatAmount(unreportedRevenue, currentCurrency)
                : formatAbbreviatedCurrency(unreportedRevenue)}
            </span>
          </span>
          <RangePicker
            picker='year'
            value={effectiveRange}
            onChange={vals => setDateRange(vals ? [vals[0], vals[1]] : [null, null])}
            allowClear
          />
        </Flex>
      }
    >
      <Flex gap={16} style={{ marginBottom: 12 }}>
        {legendItems.map(l => (
          <LegendDot key={l.label} color={l.color} label={l.label} />
        ))}
      </Flex>

      <div style={{ width: '100%', height: 205 }}>
        <ResponsiveContainer width='100%' height='100%'>
          <BarChart data={filteredChartData} barGap={4} barCategoryGap={8}>
            <CartesianGrid vertical={false} stroke='#f0f0f0' />
            <XAxis
              dataKey='date'
              tickLine={false}
              axisLine={{ stroke: '#f0f0f0' }}
              tick={{ fontSize: 14, fontFamily: 'Source Sans Pro', fill: '#546E7A' }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 14, fontFamily: 'Source Sans Pro', fill: '#546E7A' }}
              tickFormatter={formatYAxis}
            />
            <Tooltip
              cursor={false}
              contentStyle={{ fontFamily: 'Source Sans Pro' }}
              formatter={(value: number, name: string) => [formatAbbreviatedCurrency(value), name]}
            />
            <Legend wrapperStyle={{ display: 'none' }} />
            <Bar dataKey='reported' fill={CHART_COLORS.reported} radius={[5, 5, 0, 0]} barSize={10} name='Reported' />
            <Bar
              dataKey='documented'
              fill={CHART_COLORS.documented}
              radius={[5, 5, 0, 0]}
              barSize={10}
              name='Documented'
            />
            {hasEstimated && (
              <Bar
                dataKey='estimated'
                fill={CHART_COLORS.estimated}
                radius={[5, 5, 0, 0]}
                barSize={10}
                name='Estimated'
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </SectionCard>
  )
}

export default Overview
